// src/utils/invoiceRefundUi.js — refundscreen-7c3e5a18 (R1d-1)
// What the invoice screen SHOWS around refunds and credit notes. Display decisions only — every
// refund is decided by the server (refundInvoicePayment, Owner / Admin / Manager).
//
// THE THREE RULES
//   1. Refund is offered to Owner / Admin / Manager only, in every company (BACKLOG row 19,
//      Marcus 2026-10-10: for anyone else it only ever led to "permission denied").
//   2. A company WITHOUT the new refund flow (organizations/{org}.invoiceRefundFlowEnabled, set
//      only by Marcus) keeps its old line rule word for word — legacyRefundOffered below is the
//      condition InvoiceDetailsView used before this seam. Only rule 1 is added on top.
//   3. A company WITH it offers Refund on real payment lines only (the server's isCashPaymentRow):
//      never on a credit-note, refund, applied-credit or deposit line. A deposit line points the
//      operator to the estimate instead (row 17).
//
// CREDIT NOTES are data-driven, not flag-driven: invoiceCreditView is null for every invoice
// without a credit note, and the screen then renders exactly as it did before.
import { canRefundInvoice } from './permissions';
import { isCashPaymentRow } from './invoiceRefund';
import { summarizeCredits, summaryLines, paymentLabel } from './invoiceCreditSummary';

const REFUND_FLOW_FLAG = 'invoiceRefundFlowEnabled';

/** True only when the server-owned switch is on for this company. The web never writes it. */
const isRefundFlowOn = (companySettings) => !!companySettings && companySettings[REFUND_FLOW_FLAG] === true;

/** The line rule InvoiceDetailsView used before R1d (sdrefund3-invrow-7c2d9f41), unchanged. */
const legacyRefundOffered = (row, invoiceNetPaid) => {
    const isRefundRow = row.isRefund || Number(row.amount) < 0;
    const isCreditRow = row.isCredit === true;
    return !!(!isRefundRow && !isCreditRow && invoiceNetPaid > 0);
};

/**
 * What one payment-history line offers.
 * @returns {'refund' | 'depositHint' | 'none'}
 */
function refundRowAction(row, { flagOn, user, invoiceNetPaid }) {
    if (!row || !canRefundInvoice(user)) return 'none';
    if (!flagOn) return legacyRefundOffered(row, invoiceNetPaid) ? 'refund' : 'none';
    if (row.depositQuoteId && Number(row.amount) > 0) return 'depositHint';
    return isCashPaymentRow(row) && invoiceNetPaid > 0 ? 'refund' : 'none';
}

const DEPOSIT_HINT = 'Deposit — refund it from the estimate';

/**
 * The credit-note view of an invoice: the shared summary rule (same words as the PDF, the emails
 * and the portal) plus a lookup for naming refund lines. NULL when the invoice has no credit note.
 */
function invoiceCreditView(invoice) {
    const summary = summarizeCredits(invoice);
    if (!summary) return null;
    const numberById = {};
    for (const c of summary.credits) if (c.creditNoteId) numberById[c.creditNoteId] = c.number;
    return { summary, lines: summaryLines(summary), numberById };
}

/**
 * How one payment-history line is drawn.
 *   - On an invoice with credit notes the internal +credit-note line is hidden (the summary shows it).
 *   - A refund is named by how it was made ("Refund by cheque") on an invoice with credit notes,
 *     and on any refund made by the new flow (it carries a refundId).
 *   - refundstatus-8a3d6f12 (R1d-3): a new-flow refund's Reference column says "Credit note CN-0009"
 *     (or nothing for a returned payment) instead of repeating the Method column. reference null =
 *     show the line's own stored reference, as before.
 * A line that is neither (every line of a company without the new flow) is drawn as it always was.
 */
const PLAIN_ROW = Object.freeze({ hidden: false, refundLabel: null, reference: null, refundId: null, creditNoteId: null, creditNoteNumber: null });
function paymentRowView(row, creditView) {
    if (!row) return PLAIN_ROW;
    if (creditView && row.isCreditNote === true) return { ...PLAIN_ROW, hidden: true };
    const isRefundRow = (row.isRefund || Number(row.amount) < 0) && row.isCreditReversal !== true;
    const newFlow = !!row.refundId;
    if (!isRefundRow || (!creditView && !newFlow)) return PLAIN_ROW;
    const creditNoteNumber = (creditView && row.creditNoteId && creditView.numberById[row.creditNoteId]) || null;
    return {
        hidden: false, refundLabel: paymentLabel(row, {}),
        reference: newFlow ? (creditNoteNumber ? `Credit note ${creditNoteNumber}` : '') : null,
        refundId: row.refundId || null, creditNoteId: creditNoteNumber ? row.creditNoteId : null, creditNoteNumber,
    };
}

export { REFUND_FLOW_FLAG, DEPOSIT_HINT, isRefundFlowOn, legacyRefundOffered, refundRowAction, invoiceCreditView, paymentRowView };
